/**
 * 敏感词过滤工具页：支持手动输入或上传 CSV，在本地批量删除标题中的敏感词。
 * 全程本地处理，数据不上传，保护用户隐私。
 */

import { useEffect, useId, useRef, useState } from 'react'
import type { ChangeEvent, ReactElement } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import * as XLSX from 'xlsx'
import {
  CheckCircle2,
  Download,
  Info,
  Loader2,
  ScanText,
  ShieldAlert,
  Sparkles,
  Trash2,
  Upload,
} from 'lucide-react'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

/** 标题输入模式 */
type InputMode = 'text' | 'csv'

/** 处理状态 */
type ProcessingStatus = 'idle' | 'running' | 'completed'

/** CSV 行记录 */
type CsvRecord = Record<string, string>

/** 标题最大数量 */
const MAX_TITLE_COUNT = 5000

/** 每批处理数量 */
const PROCESSING_BATCH_SIZE = 120

/** 列预览最大字符长度 */
const COLUMN_PREVIEW_MAX_LENGTH = 45

/** 导出净化列名 */
const SANITIZED_TITLE_COLUMN = '净化后标题'

/** 敏感词本地记录存储键 */
const STORED_SENSITIVE_WORDS_STORAGE_KEY = 'sensitive-word-filter-keywords' as const

/** 敏感词本地记录版本号 */
const STORED_SENSITIVE_WORDS_STORAGE_VERSION = 1 as const

/** 默认的空进度状态 */
const IDLE_PROGRESS = Object.freeze({
  status: 'idle',
  total: 0,
  processed: 0,
  percent: 0,
  matchedTitles: 0,
  removalCount: 0,
}) satisfies ProcessingProgress

/**
 * CSV 数据集
 */
interface CsvDataset {
  filename: string
  headers: string[]
  rows: CsvRecord[]
}

/**
 * 待处理标题项
 */
interface PreparedTitleItem {
  id: string
  originalTitle: string
  sourceIndex: number
  sourceLabel: string
}

/**
 * 处理后的标题项
 */
interface ProcessedTitleItem extends PreparedTitleItem {
  cleanedTitle: string
  removedKeywords: string[]
  removalCount: number
}

/**
 * 处理进度信息
 */
interface ProcessingProgress {
  status: ProcessingStatus
  total: number
  processed: number
  percent: number
  matchedTitles: number
  removalCount: number
}

/**
 * 清洗结果
 */
interface SanitizedTitleResult {
  cleanedTitle: string
  removedKeywords: string[]
  removalCount: number
}

/**
 * 浏览器本地保存的敏感词记录。
 */
interface StoredSensitiveWordsPayload {
  version: typeof STORED_SENSITIVE_WORDS_STORAGE_VERSION
  words: string[]
  updatedAt: string
}

/**
 * 浏览器本地敏感词读写结果。
 */
interface StoredSensitiveWordsOperationResult {
  words: string[]
  persisted: boolean
}

/**
 * 判断字符串是否为合法输入模式。
 * @param value 待判断的字符串
 * @returns 是否为合法的输入模式
 */
function isInputMode(value: string): value is InputMode {
  return value === 'text' || value === 'csv'
}

/**
 * 生成空闲进度对象。
 * @returns 空闲状态的进度对象
 */
function createIdleProgress(): ProcessingProgress {
  return {
    ...IDLE_PROGRESS,
  }
}

/**
 * 将多行文本规范化为条目数组。
 * @param value 原始文本
 * @returns 去除空行与首尾空格后的条目数组
 */
function normalizeLineEntries(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter((item) => item !== '')
}

/**
 * 生成唯一的 CSV 表头。
 * @param rawHeaders 原始表头数组
 * @returns 去重并补齐后的表头数组
 */
function normalizeCsvHeaders(rawHeaders: readonly string[]): string[] {
  const duplicatedCounter = new Map<string, number>()

  return rawHeaders.map((header, index) => {
    const normalizedHeader = header.trim() !== '' ? header.trim() : `列${String(index + 1)}`
    const currentCount = (duplicatedCounter.get(normalizedHeader) ?? 0) + 1
    duplicatedCounter.set(normalizedHeader, currentCount)

    if (currentCount === 1) {
      return normalizedHeader
    }

    return `${normalizedHeader}_${String(currentCount)}`
  })
}

/**
 * 解析 CSV 文本为结构化数据。
 * @param content CSV 原始内容
 * @param filename 文件名
 * @returns 结构化 CSV 数据
 */
function parseCsvText(content: string, filename: string): CsvDataset {
  const normalizedContent = content.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const rows: string[][] = []
  let currentRow: string[] = []
  let currentCell = ''
  let insideQuotes = false

  for (let index = 0; index < normalizedContent.length; index += 1) {
    const currentCharacter = normalizedContent.charAt(index)
    const nextCharacter = normalizedContent.charAt(index + 1)

    if (insideQuotes) {
      if (currentCharacter === '"') {
        if (nextCharacter === '"') {
          currentCell += '"'
          index += 1
        } else {
          insideQuotes = false
        }
      } else {
        currentCell += currentCharacter
      }
      continue
    }

    if (currentCharacter === '"') {
      insideQuotes = true
      continue
    }

    if (currentCharacter === ',') {
      currentRow.push(currentCell)
      currentCell = ''
      continue
    }

    if (currentCharacter === '\n') {
      currentRow.push(currentCell)
      rows.push(currentRow)
      currentRow = []
      currentCell = ''
      continue
    }

    currentCell += currentCharacter
  }

  const hasLastRowContent = currentCell !== '' || currentRow.length > 0
  if (hasLastRowContent) {
    currentRow.push(currentCell)
    rows.push(currentRow)
  }

  const meaningfulRows = rows.filter((row, index) => index === 0 || row.some((cell) => cell.trim() !== ''))

  if (meaningfulRows.length === 0) {
    throw new Error('CSV 文件为空，请重新上传。')
  }

  const headerRow = meaningfulRows[0]
  if (headerRow === undefined) {
    throw new Error('CSV 表头读取失败，请检查文件内容。')
  }

  const dataRows = meaningfulRows.slice(1)
  const headers = normalizeCsvHeaders(headerRow)
  const bodyRows = dataRows.map((row) => {
    const record: CsvRecord = {}

    headers.forEach((header, index) => {
      record[header] = row[index] ?? ''
    })

    return record
  })

  return {
    filename,
    headers,
    rows: bodyRows,
  }
}

/**
 * 获取指定 CSV 列中的有效值数量。
 * @param dataset CSV 数据
 * @param columnName 列名
 * @returns 非空值数量
 */
function getNonEmptyCsvValueCount(dataset: CsvDataset | null, columnName: string): number {
  if (dataset === null || columnName === '') {
    return 0
  }

  return dataset.rows.reduce((count, row) => {
    return row[columnName]?.trim() !== '' ? count + 1 : count
  }, 0)
}

/**
 * 将文本标题转换为待处理数组。
 * @param titles 标题列表
 * @returns 待处理标题项数组
 */
function prepareTitlesFromText(titles: readonly string[]): PreparedTitleItem[] {
  return titles.map((title, index) => ({
    id: `text-${String(index + 1)}`,
    originalTitle: title,
    sourceIndex: index,
    sourceLabel: `第 ${String(index + 1)} 行`,
  }))
}

/**
 * 将 CSV 标题列转换为待处理数组。
 * @param dataset CSV 数据
 * @param columnName 标题列名
 * @returns 待处理标题项数组
 */
function prepareTitlesFromCsv(dataset: CsvDataset, columnName: string): PreparedTitleItem[] {
  return dataset.rows.flatMap((row, index) => {
    const title = row[columnName]?.trim() ?? ''

    if (title === '') {
      return []
    }

    return [
      {
        id: `csv-${String(index + 1)}`,
        originalTitle: title,
        sourceIndex: index,
        sourceLabel: `CSV 第 ${String(index + 2)} 行`,
      },
    ]
  })
}

/**
 * 规范化敏感词数组并按长度倒序排列。
 * @param words 原始敏感词数组
 * @returns 去重后的敏感词数组
 */
function normalizeSensitiveWords(words: readonly string[]): string[] {
  const uniqueWords = Array.from(
    new Set(
      words
        .map((word) => word.trim())
        .filter((word) => word !== '')
    )
  )

  return uniqueWords.sort((left, right) => {
    if (right.length !== left.length) {
      return right.length - left.length
    }

    return left.localeCompare(right, 'zh-CN')
  })
}

/**
 * 从文本内容中提取敏感词列表。
 * @param value 多行文本内容
 * @returns 敏感词列表
 */
function buildSensitiveWordsFromText(value: string): string[] {
  return normalizeSensitiveWords(normalizeLineEntries(value))
}

/**
 * 创建空的本地敏感词记录对象。
 * @returns 空的本地敏感词记录
 */
function createEmptyStoredSensitiveWordsPayload(): StoredSensitiveWordsPayload {
  return {
    version: STORED_SENSITIVE_WORDS_STORAGE_VERSION,
    words: [],
    updatedAt: '',
  }
}

/**
 * 判断对象是否为合法的本地敏感词记录。
 * @param value 待判断的值
 * @returns 是否为合法记录
 */
function isStoredSensitiveWordsPayload(value: unknown): value is StoredSensitiveWordsPayload {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  const payload = value as Record<string, unknown>

  return (
    payload['version'] === STORED_SENSITIVE_WORDS_STORAGE_VERSION &&
    typeof payload['updatedAt'] === 'string' &&
    Array.isArray(payload['words']) &&
    payload['words'].every((word) => typeof word === 'string')
  )
}

/**
 * 读取浏览器本地保存的敏感词记录。
 * @returns 本地敏感词记录
 */
function loadStoredSensitiveWords(): StoredSensitiveWordsPayload {
  const emptyPayload = createEmptyStoredSensitiveWordsPayload()

  try {
    const rawValue = localStorage.getItem(STORED_SENSITIVE_WORDS_STORAGE_KEY)

    if (rawValue === null) {
      return emptyPayload
    }

    const parsedValue: unknown = JSON.parse(rawValue)

    if (!isStoredSensitiveWordsPayload(parsedValue)) {
      localStorage.removeItem(STORED_SENSITIVE_WORDS_STORAGE_KEY)
      return emptyPayload
    }

    return {
      ...parsedValue,
      words: normalizeSensitiveWords(parsedValue.words),
    }
  } catch {
    return emptyPayload
  }
}

/**
 * 保存敏感词到浏览器本地记录。
 * @param words 待保存的敏感词数组
 * @returns 保存结果
 */
function saveStoredSensitiveWords(words: readonly string[]): StoredSensitiveWordsOperationResult {
  const normalizedWords = normalizeSensitiveWords(words)

  try {
    const payload: StoredSensitiveWordsPayload = {
      version: STORED_SENSITIVE_WORDS_STORAGE_VERSION,
      words: normalizedWords,
      updatedAt: new Date().toISOString(),
    }

    localStorage.setItem(STORED_SENSITIVE_WORDS_STORAGE_KEY, JSON.stringify(payload))

    return {
      words: normalizedWords,
      persisted: true,
    }
  } catch {
    return {
      words: normalizedWords,
      persisted: false,
    }
  }
}

/**
 * 将敏感词增量合并到浏览器本地记录。
 * @param words 待追加的敏感词数组
 * @returns 合并后的保存结果
 */
function mergeStoredSensitiveWords(words: readonly string[]): StoredSensitiveWordsOperationResult {
  const nextWords = normalizeSensitiveWords(words)

  if (nextWords.length === 0) {
    return {
      words: loadStoredSensitiveWords().words,
      persisted: true,
    }
  }

  const storedPayload = loadStoredSensitiveWords()
  const mergedWords = normalizeSensitiveWords([...storedPayload.words, ...nextWords])

  if (mergedWords.length === storedPayload.words.length) {
    return {
      words: storedPayload.words,
      persisted: true,
    }
  }

  return saveStoredSensitiveWords(mergedWords)
}

/**
 * 清理浏览器本地保存的敏感词记录。
 * @returns 是否清理成功
 */
function clearStoredSensitiveWords(): boolean {
  try {
    localStorage.removeItem(STORED_SENSITIVE_WORDS_STORAGE_KEY)
    return true
  } catch {
    return false
  }
}

/**
 * 清洗单条标题中的敏感词。
 * @param title 原始标题
 * @param sensitiveWords 敏感词列表
 * @returns 清洗后的标题与命中信息
 */
function sanitizeTitle(title: string, sensitiveWords: readonly string[]): SanitizedTitleResult {
  let cleanedTitle = title
  const removedKeywords: string[] = []
  let removalCount = 0

  sensitiveWords.forEach((word) => {
    if (word === '') {
      return
    }

    const titleParts = cleanedTitle.split(word)
    const hitCount = titleParts.length - 1

    if (hitCount <= 0) {
      return
    }

    cleanedTitle = titleParts.join('')
    removedKeywords.push(word)
    removalCount += hitCount
  })

  return {
    cleanedTitle,
    removedKeywords,
    removalCount,
  }
}

/**
 * 转义 CSV 单元格内容。
 * @param value 单元格原始值
 * @returns 已转义的字符串
 */
function escapeCsvValue(value: string): string {
  const normalizedValue = value.replace(/"/g, '""')
  const shouldWrap = /[",\n\r]/.test(normalizedValue) || /^\s|\s$/.test(normalizedValue)

  return shouldWrap ? `"${normalizedValue}"` : normalizedValue
}

/**
 * 根据表头与行数据构建 CSV 文件内容。
 * @param headers 表头数组
 * @param rows 行数据数组
 * @returns 带 UTF-8 BOM 的 CSV 文本
 */
function buildCsvContent(headers: readonly string[], rows: readonly CsvRecord[]): string {
  const headerLine = headers.map((header) => escapeCsvValue(header)).join(',')
  const bodyLines = rows.map((row) =>
    headers.map((header) => escapeCsvValue(row[header] ?? '')).join(',')
  )

  return `\uFEFF${[headerLine, ...bodyLines].join('\r\n')}`
}

/**
 * 生成导出文件名。
 * @param prefix 文件名前缀
 * @returns 含时间戳的文件名
 */
function buildExportFilename(prefix: string): string {
  const currentTime = new Date()
  const year = String(currentTime.getFullYear())
  const month = String(currentTime.getMonth() + 1).padStart(2, '0')
  const day = String(currentTime.getDate()).padStart(2, '0')
  const hour = String(currentTime.getHours()).padStart(2, '0')
  const minute = String(currentTime.getMinutes()).padStart(2, '0')
  const second = String(currentTime.getSeconds()).padStart(2, '0')

  return `${prefix}-${year}${month}${day}-${hour}${minute}${second}.csv`
}

/**
 * 触发浏览器下载 CSV 文件。
 * @param filename 文件名
 * @param content CSV 文本内容
 */
function downloadCsvFile(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const objectURL = URL.createObjectURL(blob)
  const anchorElement = document.createElement('a')
  anchorElement.href = objectURL
  anchorElement.download = filename
  anchorElement.click()
  URL.revokeObjectURL(objectURL)
}

/**
 * 等待下一帧渲染，保证进度条可见更新。
 * @returns 下一帧完成后的 Promise
 */
function waitForNextFrame(): Promise<void> {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      resolve()
    })
  })
}

/**
 * 处理上传文件并解析为文本或 CSV 数据集。
 * 支持 TXT、CSV、XLSX、XLS 格式。
 * @param file 上传的文件
 * @param setTextFn 设置文本内容的函数
 * @param setCsvDataFn 设置 CSV 数据集的函数
 * @param setColumnFn 设置选中列的函数
 * @param setModeFn 设置输入模式的函数
 * @returns 解析完成的 Promise
 */
async function handleFileUpload(
  file: File,
  setTextFn: (text: string) => void,
  setCsvDataFn: (data: CsvDataset | null) => void,
  setColumnFn: (col: string) => void,
  setModeFn: (mode: InputMode) => void,
): Promise<void> {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''

  if (extension === 'txt') {
    const textContent = await file.text()
    const lines = textContent
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line !== '')
    setTextFn(lines.join('\n'))
    setModeFn('text')
    toast.success(`已导入 ${lines.length.toLocaleString()} 条数据`)
    return
  }

  if (extension === 'xlsx' || extension === 'xls') {
    const buffer = await file.arrayBuffer()
    const workbook = XLSX.read(buffer, { type: 'array' })
    const firstSheetName = workbook.SheetNames[0]

    if (firstSheetName === undefined) {
      throw new Error('Excel 文件中没有工作表，请检查文件内容。')
    }

    const sheet = workbook.Sheets[firstSheetName]

    if (sheet === undefined) {
      throw new Error('无法读取 Excel 工作表，请检查文件内容。')
    }

    const csvString = XLSX.utils.sheet_to_csv(sheet)
    const dataset = parseCsvText(csvString, file.name)
    setCsvDataFn(dataset)
    setColumnFn(dataset.headers[0] ?? '')
    setModeFn('csv')
    toast.success(`Excel 文件已加载：${file.name}`)
    return
  }

  // Default: treat as CSV
  const dataset = parseCsvText(await file.text(), file.name)
  setCsvDataFn(dataset)
  setColumnFn(dataset.headers[0] ?? '')
  setModeFn('csv')
  toast.success(`CSV 文件已加载：${dataset.filename}`)
}

/**
 * 敏感词过滤工具页组件。
 * @returns 页面 ReactElement
 */
export function SensitiveWordFilterPage(): ReactElement {
  const titleFileInputID = useId()
  const keywordFileInputID = useId()
  const hasInitializedStoredKeywordsRef = useRef<boolean>(false)
  const [titleInputMode, setTitleInputMode] = useState<InputMode>('text')
  const [keywordInputMode, setKeywordInputMode] = useState<InputMode>('text')
  const [titleText, setTitleText] = useState<string>('')
  const [keywordText, setKeywordText] = useState<string>('')
  const [titleCsvData, setTitleCsvData] = useState<CsvDataset | null>(null)
  const [keywordCsvData, setKeywordCsvData] = useState<CsvDataset | null>(null)
  const [selectedTitleColumn, setSelectedTitleColumn] = useState<string>('')
  const [selectedKeywordColumn, setSelectedKeywordColumn] = useState<string>('')
  const [keywordFullLines, setKeywordFullLines] = useState<string[]>([])
  const [isKeywordFromFile, setIsKeywordFromFile] = useState<boolean>(false)
  const [storedKeywordWords, setStoredKeywordWords] = useState<string[]>(
    (): string[] => loadStoredSensitiveWords().words
  )
  const [hasAutoLoadedStoredKeywords, setHasAutoLoadedStoredKeywords] = useState<boolean>(false)
  const [processedRows, setProcessedRows] = useState<ProcessedTitleItem[]>([])
  const [progress, setProgress] = useState<ProcessingProgress>(createIdleProgress)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)

  /**
   * 标记用户是否已尝试执行过滤操作。
   * 仅在用户点击"开始过滤"按钮后才显示校验提示。
   */
  const [hasAttemptedProcess, setHasAttemptedProcess] = useState<boolean>(false)

  const manualTitleEntries = normalizeLineEntries(titleText)
  const manualKeywordEntries = buildSensitiveWordsFromText(keywordText)
  const activeTitleCount =
    titleInputMode === 'text'
      ? manualTitleEntries.length
      : getNonEmptyCsvValueCount(titleCsvData, selectedTitleColumn)
  const activeKeywordCount =
    keywordInputMode === 'text'
      ? manualKeywordEntries.length
      : isKeywordFromFile
        ? keywordFullLines.length
        : getNonEmptyCsvValueCount(keywordCsvData, selectedKeywordColumn)
  const hasStoredKeywordRecords = storedKeywordWords.length > 0
  const isTitleLimitExceeded = activeTitleCount > MAX_TITLE_COUNT

  /**
   * 将敏感词增量保存到浏览器本地记录，并同步本地状态。
   * @param words 待保存的敏感词数组
   */
  function persistKeywordsIncrementally(words: readonly string[]): void {
    const persistedResult = mergeStoredSensitiveWords(words)

    if (persistedResult.persisted) {
      setStoredKeywordWords(persistedResult.words)
    }
  }

  /**
   * 获取标题列的预览内容。
   * @param columnName 列名
   * @returns 前2行数据的预览数组
   */
  function getTitleColumnPreview(columnName: string): string[] {
    if (titleCsvData === null || columnName === '') {
      return []
    }

    return titleCsvData.rows.slice(0, 2).map((row, index) => {
      const value = row[columnName]?.trim() ?? ''
      if (value === '') {
        return `${String(index + 1)}. (空)`
      }
      if (value.length > COLUMN_PREVIEW_MAX_LENGTH) {
        return `${String(index + 1)}. "${value.substring(0, COLUMN_PREVIEW_MAX_LENGTH)}..."`
      }
      return `${String(index + 1)}. "${value}"`
    })
  }

  const validationMessages: string[] = []

  if (titleInputMode === 'csv' && titleCsvData === null) {
    validationMessages.push('请先上传标题 CSV。')
  }

  if (titleInputMode === 'csv' && titleCsvData !== null && selectedTitleColumn === '') {
    validationMessages.push('请选择标题列。')
  }

  if (keywordInputMode === 'csv' && keywordCsvData === null && !isKeywordFromFile) {
    validationMessages.push('请先上传敏感词文件。')
  }

  if (activeTitleCount === 0) {
    validationMessages.push('请先准备至少 1 条标题。')
  }

  if (isTitleLimitExceeded) {
    validationMessages.push(`标题数量已超过 ${MAX_TITLE_COUNT.toLocaleString()} 条上限，请拆分后再处理。`)
  }

  if (activeKeywordCount === 0) {
    validationMessages.push('请先输入敏感词或上传敏感词 CSV。')
  }

  const canProcess = !isProcessing && validationMessages.length === 0

  /**
   * 当输入源发生变化时，清空旧结果，避免展示过期数据。
   */
  useEffect((): void => {
    setProcessedRows([])
    setProgress(createIdleProgress())
  }, [
    titleInputMode,
    keywordInputMode,
    titleText,
    keywordText,
    titleCsvData,
    keywordCsvData,
    selectedTitleColumn,
    selectedKeywordColumn,
    keywordFullLines,
    isKeywordFromFile,
  ])

  /**
   * 首次进入页面时，自动加载浏览器本地保存的敏感词。
   */
  useEffect((): void => {
    if (hasInitializedStoredKeywordsRef.current) {
      return
    }

    hasInitializedStoredKeywordsRef.current = true

    if (
      storedKeywordWords.length === 0 ||
      keywordText.trim() !== '' ||
      keywordFullLines.length > 0 ||
      isKeywordFromFile
    ) {
      return
    }

    setKeywordInputMode('text')
    setKeywordText(storedKeywordWords.join('\n'))
    setHasAutoLoadedStoredKeywords(true)
  }, [isKeywordFromFile, keywordFullLines.length, keywordText, storedKeywordWords])

  /**
   * 处理标题文件上传（支持 CSV/TXT/Excel）。
   * @param event 文件输入事件
   */
  async function handleTitleFileUpload(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0]

    if (file === undefined) {
      return
    }

    try {
      await handleFileUpload(
        file,
        setTitleText,
        setTitleCsvData,
        setSelectedTitleColumn,
        setTitleInputMode,
      )
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '文件解析失败，请检查文件格式。')
    } finally {
      event.target.value = ''
    }
  }

  /**
   * 处理敏感词文件上传（支持 TXT/CSV/Excel，仅限单列）。
   * @param event 文件输入事件
   */
  async function handleKeywordFileUpload(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0]

    if (file === undefined) {
      return
    }

    try {
      const extension = file.name.split('.').pop()?.toLowerCase() ?? ''

      // TXT 文件直接按行读取
      if (extension === 'txt') {
        const textContent = await file.text()
        const lines = textContent
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter((line) => line !== '')
        setKeywordFullLines(lines)
        setIsKeywordFromFile(true)
        setKeywordCsvData(null)
        setSelectedKeywordColumn('')
        persistKeywordsIncrementally(lines)
        toast.success(`已导入 ${lines.length.toLocaleString()} 个敏感词`)
        return
      }

      // CSV/Excel 文件需检查是否为单列
      let dataset: CsvDataset

      if (extension === 'xlsx' || extension === 'xls') {
        const buffer = await file.arrayBuffer()
        const workbook = XLSX.read(buffer, { type: 'array' })
        const firstSheetName = workbook.SheetNames[0]

        if (firstSheetName === undefined) {
          throw new Error('Excel 文件中没有工作表，请检查文件内容。')
        }

        const sheet = workbook.Sheets[firstSheetName]

        if (sheet === undefined) {
          throw new Error('无法读取 Excel 工作表，请检查文件内容。')
        }

        const csvString = XLSX.utils.sheet_to_csv(sheet)
        dataset = parseCsvText(csvString, file.name)
      } else {
        dataset = parseCsvText(await file.text(), file.name)
      }

      // 敏感词文件必须是单列格式
      if (dataset.headers.length > 1) {
        toast.error('敏感词文件应为单列格式（每行一个敏感词），当前文件包含多列数据')
        return
      }

      // 自动使用第一列（唯一列）
      const columnName = dataset.headers[0] ?? ''
      const words = dataset.rows.map((row) => row[columnName]?.trim() ?? '').filter((word) => word !== '')
      setKeywordFullLines(words)
      setIsKeywordFromFile(true)
      setKeywordCsvData(null)
      setSelectedKeywordColumn('')
      persistKeywordsIncrementally(words)
      toast.success(`已导入 ${words.length.toLocaleString()} 个敏感词`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '文件解析失败，请检查文件格式。')
    } finally {
      event.target.value = ''
    }
  }

  /**
   * 将文本输入中的敏感词增量保存到浏览器本地记录。
   */
  function handleKeywordTextBlur(): void {
    if (keywordText.trim() === '') {
      return
    }

    persistKeywordsIncrementally(buildSensitiveWordsFromText(keywordText))
  }

  /**
   * 开始执行敏感词过滤。
   */
  async function handleProcess(): Promise<void> {
    setHasAttemptedProcess(true)

    if (!canProcess) {
      toast.error(validationMessages[0] ?? '当前数据不完整，无法开始处理。')
      return
    }

    const titleItems =
      titleInputMode === 'text'
        ? prepareTitlesFromText(manualTitleEntries)
        : titleCsvData !== null
          ? prepareTitlesFromCsv(titleCsvData, selectedTitleColumn)
          : []

    // 敏感词来源：优先使用文件导入的完整列表，否则解析文本框内容
    const sensitiveWords = isKeywordFromFile && keywordFullLines.length > 0
      ? normalizeSensitiveWords(keywordFullLines)
      : manualKeywordEntries

    if (titleItems.length === 0) {
      toast.error('没有可处理的有效标题。')
      return
    }

    if (sensitiveWords.length === 0) {
      toast.error('没有可用的敏感词，请检查输入内容。')
      return
    }

    persistKeywordsIncrementally(sensitiveWords)
    setIsProcessing(true)
    setProcessedRows([])
    setProgress({
      status: 'running',
      total: titleItems.length,
      processed: 0,
      percent: 0,
      matchedTitles: 0,
      removalCount: 0,
    })

    const nextRows: ProcessedTitleItem[] = []
    let matchedTitles = 0
    let removalCount = 0

    try {
      for (let startIndex = 0; startIndex < titleItems.length; startIndex += PROCESSING_BATCH_SIZE) {
        const batchItems = titleItems.slice(startIndex, startIndex + PROCESSING_BATCH_SIZE)

        batchItems.forEach((item) => {
          const sanitizedResult = sanitizeTitle(item.originalTitle, sensitiveWords)

          if (sanitizedResult.removalCount > 0) {
            matchedTitles += 1
            removalCount += sanitizedResult.removalCount
          }

          nextRows.push({
            ...item,
            cleanedTitle: sanitizedResult.cleanedTitle,
            removedKeywords: sanitizedResult.removedKeywords,
            removalCount: sanitizedResult.removalCount,
          })
        })

        const processedCount = Math.min(startIndex + batchItems.length, titleItems.length)
        setProgress({
          status: 'running',
          total: titleItems.length,
          processed: processedCount,
          percent: Math.round((processedCount / titleItems.length) * 100),
          matchedTitles,
          removalCount,
        })

        await waitForNextFrame()
      }

      setProcessedRows(nextRows)
      setProgress({
        status: 'completed',
        total: titleItems.length,
        processed: titleItems.length,
        percent: 100,
        matchedTitles,
        removalCount,
      })
      toast.success(`处理完成，共生成 ${titleItems.length.toLocaleString()} 条结果。`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '处理失败，请稍后重试。')
      setProgress(createIdleProgress())
    } finally {
      setIsProcessing(false)
    }
  }

  /**
   * 导出 CSV 结果文件。
   */
  function handleExportResults(): void {
    if (processedRows.length === 0) {
      toast.error('当前没有可导出的结果。')
      return
    }

    if (titleInputMode === 'csv' && titleCsvData !== null) {
      const cleanedTitleMap = new Map<number, string>(
        processedRows.map((row) => [row.sourceIndex, row.cleanedTitle])
      )
      const exportHeaders = [...titleCsvData.headers, SANITIZED_TITLE_COLUMN]
      const exportRows = titleCsvData.rows.map((row, index) => ({
        ...row,
        [SANITIZED_TITLE_COLUMN]: cleanedTitleMap.get(index) ?? '',
      }))

      downloadCsvFile(
        buildExportFilename('敏感词过滤结果'),
        buildCsvContent(exportHeaders, exportRows)
      )
      toast.success('CSV 已导出。')
      return
    }

    const exportRows = processedRows.map((row) => ({
      [SANITIZED_TITLE_COLUMN]: row.cleanedTitle,
    }))

    downloadCsvFile(
      buildExportFilename('敏感词过滤结果'),
      buildCsvContent([SANITIZED_TITLE_COLUMN], exportRows)
    )
    toast.success('CSV 已导出。')
  }

  /**
   * 清空当前工具页中的所有输入与结果。
   */
  function handleResetAll(): void {
    setTitleInputMode('text')
    setKeywordInputMode('text')
    setTitleText('')
    setKeywordText('')
    setTitleCsvData(null)
    setKeywordCsvData(null)
    setSelectedTitleColumn('')
    setSelectedKeywordColumn('')
    setKeywordFullLines([])
    setIsKeywordFromFile(false)
    setHasAutoLoadedStoredKeywords(false)
    setProcessedRows([])
    setProgress(createIdleProgress())
    setIsProcessing(false)
    setHasAttemptedProcess(false)
    toast.success('已清空当前输入与结果。')
  }

  /**
   * 清理浏览器本地敏感词记录，并同步清空当前敏感词输入。
   */
  function handleClearStoredKeywords(): void {
    const isCleared = clearStoredSensitiveWords()

    if (!isCleared) {
      toast.error('清理本地敏感词记录失败，请检查浏览器存储权限。')
      return
    }

    setStoredKeywordWords([])
    setKeywordInputMode('text')
    setKeywordText('')
    setKeywordCsvData(null)
    setSelectedKeywordColumn('')
    setKeywordFullLines([])
    setIsKeywordFromFile(false)
    setHasAutoLoadedStoredKeywords(false)
    toast.success('已清理当前浏览器保存的敏感词记录。')
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mx-auto max-w-5xl space-y-6 py-6"
    >
      {/* Section 1: Header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-3 text-center"
      >
        <div className="flex items-center justify-center gap-2">
          <ShieldAlert className="h-7 w-7 text-primary" />
          <h1 className="text-2xl font-semibold tracking-tight">敏感词过滤</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          专为跨境电商卖家打造的标题净化工具，批量检测并去除商品标题中的敏感词，支持文本输入与文件导入。
        </p>
      </motion.div>

      {/* Section 2: Input Area - Two Column Layout */}
      <div className="grid grid-cols-2 items-stretch gap-6">
        {/* Left Column: Title Input */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
          className="h-full"
        >
          <Card className="h-full">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base font-medium">
                  <ScanText className="h-4 w-4 text-primary" />
                  标题
                </CardTitle>
                <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">
                  共 {activeTitleCount.toLocaleString()} 条标题
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <Tabs
                value={titleInputMode}
                onValueChange={(value): void => {
                  if (isInputMode(value)) {
                    setTitleInputMode(value)
                  }
                }}
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="text" disabled={isProcessing}>文本输入</TabsTrigger>
                  <TabsTrigger value="csv" disabled={isProcessing}>文件上传</TabsTrigger>
                </TabsList>
                <TabsContent value="text" className="pt-3">
                  <Textarea
                    value={titleText}
                    disabled={isProcessing}
                    onChange={(event): void => { setTitleText(event.target.value) }}
                    placeholder="每行一个标题..."
                    className="min-h-[240px] resize-y leading-6"
                  />
                </TabsContent>
                <TabsContent value="csv" className="space-y-3 pt-3">
                  <div className="flex items-center gap-3">
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className={cn('cursor-pointer', isProcessing && 'pointer-events-none opacity-50')}
                    >
                      <Label htmlFor={titleFileInputID} className="cursor-pointer">
                        <Upload className="mr-1.5 h-4 w-4" />
                        上传文件 (CSV/TXT/Excel)
                      </Label>
                    </Button>
                    <Input
                      id={titleFileInputID}
                      type="file"
                      accept=".csv,.txt,.xlsx,.xls"
                      disabled={isProcessing}
                      onChange={(event): void => { void handleTitleFileUpload(event) }}
                      className="hidden"
                    />
                    <span className="text-sm text-muted-foreground">
                      {titleCsvData === null ? '尚未上传' : titleCsvData.filename}
                    </span>
                  </div>
                  {titleCsvData !== null && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <Label className="text-sm text-muted-foreground">选择列：</Label>
                        <Select
                          disabled={isProcessing}
                          value={selectedTitleColumn}
                          onValueChange={setSelectedTitleColumn}
                        >
                          <SelectTrigger className="w-48">
                            <SelectValue placeholder="请选择标题列" />
                          </SelectTrigger>
                          <SelectContent>
                            {titleCsvData.headers.map((header, index) => (
                              <SelectItem key={header} value={header}>
                                第{['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'][index] ?? String(index + 1)}列
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="text-xs text-muted-foreground">默认使用第一列</span>
                      </div>
                      {selectedTitleColumn !== '' && (
                        <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
                          <p className="mb-1 text-xs text-muted-foreground">预览：</p>
                          {getTitleColumnPreview(selectedTitleColumn).map((preview) => (
                            <p key={preview} className="truncate text-foreground/80">{preview}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </motion.div>

        {/* Right Column: Keyword Input */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="h-full"
        >
          <Card className="h-full">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base font-medium">
                  <ShieldAlert className="h-4 w-4 text-amber-600" />
                  敏感词
                </CardTitle>
                <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">
                  共 {activeKeywordCount.toLocaleString()} 个敏感词
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <Tabs
                value={keywordInputMode}
                onValueChange={(value): void => {
                  if (isInputMode(value)) {
                    setKeywordInputMode(value)
                  }
                }}
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="text" disabled={isProcessing}>文本输入</TabsTrigger>
                  <TabsTrigger value="csv" disabled={isProcessing}>文件上传</TabsTrigger>
                </TabsList>
                <TabsContent value="text" className="pt-3">
                  <Textarea
                    value={keywordText}
                    rows={6}
                    disabled={isProcessing}
                    onChange={(event): void => { setKeywordText(event.target.value) }}
                    onBlur={handleKeywordTextBlur}
                    placeholder="每行一个敏感词..."
                    className="min-h-40 max-h-52 overflow-y-auto resize-y leading-6"
                  />
                </TabsContent>
                <TabsContent value="csv" className="space-y-3 pt-3">
                  <div className="flex items-center gap-3">
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className={cn('cursor-pointer', isProcessing && 'pointer-events-none opacity-50')}
                    >
                      <Label htmlFor={keywordFileInputID} className="cursor-pointer">
                        <Upload className="mr-1.5 h-4 w-4" />
                        上传文件 (TXT/CSV/Excel)
                      </Label>
                    </Button>
                    <Input
                      id={keywordFileInputID}
                      type="file"
                      accept=".csv,.txt,.xlsx,.xls"
                      disabled={isProcessing}
                      onChange={(event): void => { void handleKeywordFileUpload(event) }}
                      className="hidden"
                    />
                    {isKeywordFromFile ? (
                      <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                        ✓ 已导入 {keywordFullLines.length.toLocaleString()} 个敏感词
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">尚未上传</span>
                    )}
                  </div>
                  {isKeywordFromFile && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between rounded-lg bg-emerald-50 px-4 py-3 dark:bg-emerald-950/30">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                          <span className="font-medium text-emerald-700 dark:text-emerald-300">
                            已导入 {keywordFullLines.length.toLocaleString()} 个敏感词
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(): void => {
                            setKeywordText('')
                            setKeywordFullLines([])
                            setIsKeywordFromFile(false)
                          }}
                          disabled={isProcessing}
                          className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
                        >
                          <Trash2 className="mr-1 h-3 w-3" />
                          清空
                        </Button>
                      </div>
                      {/* 敏感词预览（最多显示15个） */}
                      <div className="rounded-md bg-muted/50 p-3">
                        <p className="mb-2 text-sm font-medium text-muted-foreground">敏感词预览：</p>
                        <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">
                          {keywordFullLines.slice(0, 20).map((word, index) => (
                            <span
                              key={index}
                              className="inline-block rounded bg-background px-2 py-1 text-xs font-mono shadow-sm"
                            >
                              {word}
                            </span>
                          ))}
                          {keywordFullLines.length > 20 && (
                            <span className="inline-block px-2 py-1 text-xs text-muted-foreground">
                              ... 还有 {keywordFullLines.length - 20} 个
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
              {hasStoredKeywordRecords && (
                <Alert className="border-amber-200/80 bg-amber-50/80 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-100">
                  <Info className="h-4 w-4" />
                  <AlertTitle className="text-amber-900 dark:text-amber-100">
                    已启用本地敏感词记录
                  </AlertTitle>
                  <AlertDescription className="text-amber-800/90 dark:text-amber-200/90">
                    <p>
                      {hasAutoLoadedStoredKeywords && keywordInputMode === 'text' && !isKeywordFromFile
                        ? `当前文本已自动加载 ${storedKeywordWords.length.toLocaleString()} 个本地敏感词。`
                        : `当前浏览器已保存 ${storedKeywordWords.length.toLocaleString()} 个本地敏感词。`}
                    </p>
                    <p>
                      这些内容来自你此前在当前浏览器上传或手动输入的敏感词；页面后续会直接从本地加载，方便继续使用。
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleClearStoredKeywords}
                        disabled={isProcessing}
                        className="border-amber-300/80 bg-transparent text-amber-900 hover:bg-amber-100 hover:text-amber-950 dark:border-amber-800 dark:text-amber-100 dark:hover:bg-amber-900/40 dark:hover:text-amber-50"
                      >
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                        清理本地记录
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Section 3: Action + Progress */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="space-y-4"
      >
        <div className="flex items-center justify-center gap-3">
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              onClick={(): void => { void handleProcess() }}
              disabled={!canProcess}
              className="min-w-32"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  处理中
                </>
              ) : (
                <>
                  <Sparkles className="mr-1.5 h-4 w-4" />
                  开始过滤
                </>
              )}
            </Button>
          </motion.div>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button variant="outline" onClick={handleResetAll} disabled={isProcessing}>
              <Trash2 className="mr-1.5 h-4 w-4" />
              清空
            </Button>
          </motion.div>
        </div>

        <AnimatePresence>
          {(progress.status === 'running' || progress.status === 'completed') && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="space-y-2"
            >
              <Progress value={progress.percent} className="h-2" />
              <p className="text-center text-sm text-muted-foreground">
                {progress.status === 'running'
                  ? `处理中... ${String(progress.percent)}% (${progress.processed.toLocaleString()}/${progress.total.toLocaleString()})`
                  : null}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {progress.status === 'completed' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, type: 'spring', bounce: 0.4 }}
              className="flex items-center justify-center gap-2 rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400"
            >
              <CheckCircle2 className="h-4 w-4" />
              处理完成！共过滤 {progress.total.toLocaleString()} 条标题，命中 {progress.matchedTitles.toLocaleString()} 条
            </motion.div>
          )}
        </AnimatePresence>

        {hasAttemptedProcess && validationMessages.length > 0 && progress.status === 'idle' && (
          <Alert variant="default" className="text-sm">
            <AlertDescription>
              {validationMessages.map((message) => (
                <p key={message}>{message}</p>
              ))}
            </AlertDescription>
          </Alert>
        )}
      </motion.div>

      {/* Section 4: Completion Stats + Export Actions */}
      <AnimatePresence>
        {progress.status === 'completed' && processedRows.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            className="rounded-lg border bg-card p-6 shadow-sm"
          >
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-6 w-6" />
                <span className="text-lg font-medium">过滤完成</span>
              </div>
              <p className="text-sm text-muted-foreground">
                共处理 {progress.total.toLocaleString()} 条标题，命中 {progress.matchedTitles.toLocaleString()} 条，删除敏感词 {progress.removalCount.toLocaleString()} 次
              </p>
              <div className="flex items-center gap-3">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    onClick={handleExportResults}
                    disabled={isProcessing}
                  >
                    <Download className="mr-1.5 h-4 w-4" />
                    导出 CSV
                  </Button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
